import { LEGACY_KEYS } from "@hachi/core";
import type { Entitlement } from "@hachi/core";
import { deviceId, newDeviceId } from "./device";
import { readJson, writeJson } from "./storage";
import { SyncError } from "./sync";
import type { Auth } from "./auth";
import type { KeyValue } from "./storage";

/** A device signed in to the account, as the server lists it. */
export interface DeviceRow {
  id: string;
  label: string;
  last_seen?: string;
  current?: boolean;
}

/** The plan as the server reports it, with the trial's end when on a free trial. */
export interface AccountEntitlement extends Entitlement {
  trial?: { plan: string; endsAt: string };
}

/** What `/api/account` answers. Unknown extra fields are kept. */
export type AccountStatus =
  | { state: "ok"; email: string; name?: string; licence?: { key?: string; max?: number; since?: string }; ent?: AccountEntitlement | null; devices?: DeviceRow[]; added?: { label: string; already?: boolean }; autoAdded?: string[]; trialStarted?: { plan: string; endsAt: string }; moved?: { label: string }; [k: string]: unknown }
  | { state: "signed-out" }
  | { state: "revoked" }
  | { state: "need-licence"; email?: string; error?: string; trialEnded?: { plan: string; endedAt: string }; [k: string]: unknown }
  | { state: "limit"; email?: string; max: number; plan?: string; devices?: DeviceRow[]; moveAt?: string | null; [k: string]: unknown }
  | { state: "key-error"; error?: string; email?: string }
  | { state: "blocked"; email?: string; reason?: string; [k: string]: unknown }
  | { state: "error"; reason?: string };

/** What this browser remembers about the account (`hachi-garage-v1-cloud`), so it can open offline. */
export interface CloudCache {
  uid?: string;
  email: string;
  name: string;
  licence?: { key?: string; max?: number; since?: string };
  ent: AccountEntitlement | null;
  devices: DeviceRow[];
  state: "ok";
  checked: number;
}

export const readCloud = (store: KeyValue) => readJson<CloudCache>(store, LEGACY_KEYS.cloud);
export const writeCloud = (store: KeyValue, c: CloudCache | null) => writeJson(store, LEGACY_KEYS.cloud, c);

export interface AccountDeps {
  store: KeyValue;
  auth: Auth;
  /** Device label sent with every call. */
  label: () => string;
  /** The account function's address (same site). */
  apiPath?: string;
  fetch?: typeof fetch;
}

/** Calls the account function with the signed-in user's token, refreshing once on 401 (`api`). */
export function createAccount({ store, auth, label, apiPath = "api/account", fetch: f }: AccountDeps) {
  const doFetch = f ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  return async function call(action: string, extra: Record<string, unknown> = {}): Promise<AccountStatus> {
    let tok = await auth.accessToken();
    if (!tok) return { state: "signed-out" };
    // v: 2 asks for the new plans; the server keeps answering the old app the old way.
    const send = (t: string) => doFetch(apiPath, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${t}` },
      body: JSON.stringify({ v: 2, action, deviceId: deviceId(store), label: label(), ...extra }),
    });
    let r = await send(tok);
    if (r.status === 401) {
      tok = await auth.accessToken(true);
      if (!tok) return { state: "signed-out" };
      r = await send(tok);
    }
    const j = (await r.json().catch(() => null)) as AccountStatus | null;
    if (r.status === 401) {
      writeJson(store, LEGACY_KEYS.session, null);
      return { state: "signed-out" };
    }
    // This browser's id belongs to someone else's account: this person gets their own, once.
    if (j?.state === "error" && j.reason === "bad-device" && !extra.retried) {
      newDeviceId(store);
      return call(action, { ...extra, retried: true });
    }
    return j ?? { state: "error", reason: "server" };
  };
}

export type AccountCall = ReturnType<typeof createAccount>;

/** Calls a database function (sync) with the signed-in user's token, refreshing once on 401 (`rpc`). */
export function createRpc({ auth, supabaseUrl, supabaseKey, fetch: f }: { auth: Auth; supabaseUrl: string; supabaseKey: string; fetch?: typeof fetch }) {
  const doFetch = f ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const base = supabaseUrl.replace(/\/+$/, "");
  return async function rpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
    let tok = await auth.accessToken();
    if (!tok) throw new SyncError("signed-out");
    const send = (t: string) => doFetch(`${base}/rest/v1/rpc/${fn}`, {
      method: "POST",
      headers: { apikey: supabaseKey, Authorization: `Bearer ${t}`, "Content-Type": "application/json" },
      body: JSON.stringify(args),
    });
    let r = await send(tok);
    if (r.status === 401) {
      tok = await auth.accessToken(true);
      if (!tok) throw new SyncError("signed-out");
      r = await send(tok);
    }
    const j = (await r.json().catch(() => null)) as (T & { message?: string }) | null;
    if (!r.ok) throw new SyncError(/device-not-active/.test(j?.message ?? "") ? "device" : r.status === 404 ? "missing" : "server");
    return j as T;
  };
}
